import { useThree } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';

import { EnhancedGLTF } from '@cartwhl/3d/gltf/types';
import { sceneDeclaresYUp } from '@cartwhl/3d/gltf/utils';
import { RotationOffsets } from '@cartwhl/common/types';

import useLoadAnimation from 'hooks/useLoadAnimation';
import useAnimations from 'threejs-utils/overrides/useAnimations';

import { DEMO_ANIMATION_PATH_2 } from '../constants';
import { fitObjectToView } from '../utils';

export default function CharacterVerificationStage({
  rotationOffsets,
  meshData,
  getPrimitive = true,
  autoCamera = true,
  isPlaying = true,
  animationPath = DEMO_ANIMATION_PATH_2,
}: {
  rotationOffsets?: RotationOffsets;
  meshData: EnhancedGLTF;
  getPrimitive?: boolean;
  autoCamera?: boolean;
  isPlaying?: boolean;
  animationPath?: string;
}) {
  const animations = useLoadAnimation([animationPath], undefined, {
    shouldLoad: true,
    rotationOffsets,
  });

  const clips = useMemo(
    () => (animations?.[0] ? [animations[0]!.animationData.clip] : []),
    [animations],
  );

  const { actions, mixer } = useAnimations(clips, meshData.scene);
  const { camera, size } = useThree();

  if (autoCamera) {
    fitObjectToView(meshData.scene, camera, size);
  }

  useEffect(() => {
    if (!actions?.animation || !mixer) return;

    actions.animation.play();

    mixer.timeScale = isPlaying ? 1 : 0;
    actions.animation.timeScale = isPlaying ? 1 : 0;
    actions.animation.paused = !isPlaying;
  }, [mixer, actions, isPlaying]);

  return getPrimitive ? (
    <primitive
      object={meshData.scene}
      rotation={
        sceneDeclaresYUp(meshData.scene) ? undefined : [-Math.PI / 2, 0, 0]
      }
    />
  ) : null;
}
